import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "./ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { Heart, MessageCircle, MoreHorizontal, Trash2 } from "lucide-react";

const PostCard = ({ post, onDelete, onUpdate, hideDelete = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLiked, setIsLiked] = useState(post.is_liked);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  const [likeAnimating, setLikeAnimating] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isOwner = user?.id === post.user_id;

  const handleLike = async () => {
    try {
      setLikeAnimating(true);
      const response = await api.post(`/posts/${post.id}/like`);
      setIsLiked(response.data.is_liked);
      setLikeCount(response.data.like_count);
      
      if (onUpdate) {
        onUpdate({
          ...post,
          is_liked: response.data.is_liked,
          like_count: response.data.like_count
        });
      }
    } catch (_error) {
      toast.error("Failed to like post");
    } finally {
      setTimeout(() => setLikeAnimating(false), 300);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/posts/${post.id}`);
      toast.success("Post deleted");
      if (onDelete) onDelete(post.id);
    } catch (_error) {
      toast.error("Failed to delete post");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleMessage = async () => {
    try {
      const response = await api.post("/conversations", { participant_id: post.user_id });
      navigate(`/messages/${response.data.id}`);
    } catch (_error) {
      toast.error("Failed to start conversation");
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getInitials = () => {
    if (post.user?.username) {
      return post.user.username.slice(0, 2).toUpperCase();
    }
    return "??";
  };

  const renderMedia = () => {
    if (!post.media_urls || post.media_urls.length === 0) return null;

    const mediaCount = post.media_urls.length;
    let gridClass = "media-grid single";
    if (mediaCount === 2) gridClass = "media-grid double";
    else if (mediaCount === 3) gridClass = "media-grid triple";
    else if (mediaCount >= 4) gridClass = "media-grid quad";

    return (
      <div className={`${gridClass} mt-3`}>
        {post.media_urls.slice(0, 4).map((url, idx) => {
          const type = post.media_types?.[idx] || "image";
          const isFirst = idx === 0 && mediaCount === 3;
          
          return (
            <div 
              key={`media-${idx}-${url.slice(-15)}`} 
              className={`relative bg-muted overflow-hidden ${isFirst ? 'row-span-2' : ''}`}
              style={{ aspectRatio: mediaCount === 1 ? '16/9' : '1' }}
            >
              {type === "video" ? (
                <div className="relative w-full h-full">
                  <video 
                    src={url} 
                    className="w-full h-full object-cover"
                    controls
                    data-testid={`post-video-${idx}`}
                  />
                </div>
              ) : (
                <img 
                  src={url} 
                  alt={`Media ${idx + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  data-testid={`post-image-${idx}`}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Card className="post-card" data-testid={`post-card-${post.id}`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <button 
              onClick={() => navigate(isOwner ? "/profile" : `/user/${post.user_id}`)}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              data-testid="post-user-link"
            >
              <Avatar>
                <AvatarImage src={post.user?.avatar} alt={post.user?.username} />
                <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="text-left">
                <p className="font-semibold text-sm leading-tight">
                  {post.user?.username || "Anonymous"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatTime(post.created_at)}
                </p>
              </div>
            </button>

            {isOwner && !hideDelete && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="post-menu-trigger">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={() => setShowDeleteDialog(true)}
                    className="text-destructive focus:text-destructive"
                    data-testid="delete-post-option"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Post
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </CardHeader>

        <CardContent className="pb-3">
          {post.content && (
            <p className="text-foreground whitespace-pre-wrap" data-testid="post-content">
              {post.content}
            </p>
          )}
          {renderMedia()}
        </CardContent>

        <CardFooter className="pt-0">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLike}
              className={`gap-2 ${isLiked ? 'text-red-500 hover:text-red-600' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="like-btn"
            >
              <Heart 
                className={`w-5 h-5 ${isLiked ? 'fill-current' : ''} ${likeAnimating ? 'like-animation' : ''}`} 
              />
              <span>{likeCount > 0 ? likeCount : ''}</span>
            </Button>

            {!isOwner && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleMessage}
                className="gap-2 text-muted-foreground hover:text-foreground"
                data-testid="message-btn"
              >
                <MessageCircle className="w-5 h-5" />
              </Button>
            )}
          </div>
        </CardFooter>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this post? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="cancel-delete-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PostCard;
